import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from '../../../../../../../shared/services/helpers/message-service/message.service';
import { ShootProofDataProvider } from '../../../../../../../shared/services/integrations/shootProof/shootProof.data.service';
import { InvoicesDataProvider } from '../../../../../../../shared/services/invoice/invoices-data-service/invoices.data.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PickTimeDataProvider } from '@services/integrations/pickTime/pickTime.data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { IntegrationsServiceProvider } from '@services/integrations/integrations.service';

@Component({
	selector: 'select-existing-gallery-modal',
	template: require('./select-existing-gallery.modal.html'),
	styles: [require('./select-existing-gallery.modal.scss')]
})
export class SelectExistingGalleryModal implements OnInit, OnDestroy {
	@Input('jobId') jobId: number;
	@Input('shootProofBrands') shootProofBrands;

	picTimeGalleries = [];
	loadingPicTimeGalleries: boolean;
	galleriesListDisable = true;
	onSubmitSpinner = false;
	selectedGalleries: string[] = [];
	selectedShootProofBrandId = -1;
	selectedGalleryId = -1;
	readonly DEFAULT_GALLERY_PRESET = [
		{ spGalleryId: -1, name: 'Choose Gallery' }
	];
	readonly LOADING_GALLERY_PRESET = [{ spGalleryId: -1, name: 'Loading...' }];
	galleriesList = this.DEFAULT_GALLERY_PRESET;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private shootProofDataService: ShootProofDataProvider,
		private pickTimeDataProvider: PickTimeDataProvider,
		private messageService: MessageService,
		private invoiceDataService: InvoicesDataProvider,
		private activeModal: NgbActiveModal,
		private integrationService: IntegrationsServiceProvider
	) {}

	ngOnInit() {
		if (!this.shootProofBrands) {
			this.getPicTimeGalleries();
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	getPicTimeGalleries() {
		this.loadingPicTimeGalleries = true;
		this.pickTimeDataProvider
			.getImportPicTimeGalleries()
			.pipe(
				takeUntil(this.unsubscribe),
				finalize(() => (this.loadingPicTimeGalleries = false))
			)
			.subscribe(
				(data): void => {
					this.picTimeGalleries = data.data;
				},
				error => this.messageService.handleError(error)
			);
	}

	selectBrand(shootProofBrandId) {
		if (shootProofBrandId != -1) {
			this.selectedShootProofBrandId = shootProofBrandId;
			this.galleriesListDisable = true;
			this.galleriesList = this.LOADING_GALLERY_PRESET;

			this.shootProofDataService
				.getGalleriesToImportV1(shootProofBrandId)
				.subscribe(
					(data): void => {
						this.galleriesListDisable = false;
						this.galleriesList = this.DEFAULT_GALLERY_PRESET.concat(data.data);
					},
					error => {
						this.galleriesList = this.DEFAULT_GALLERY_PRESET;
						this.checkIfShootProofError(error);
					}
				);
		} else {
			this.selectedShootProofBrandId = -1;
			this.selectedGalleryId = -1;
			this.galleriesList = this.DEFAULT_GALLERY_PRESET;
			this.galleriesListDisable = true;
		}
	}

	selectGallery(galleryId): void {
		if (galleryId !== -1) {
			this.selectedGalleryId = galleryId;
		}
	}

	dismiss(): void {
		this.activeModal.close();
		this.selectedGalleryId = -1;
		this.selectedShootProofBrandId = -1;
		this.galleriesList = this.DEFAULT_GALLERY_PRESET;
		this.shootProofBrands = [];
		this.selectedGalleries = [];
		this.galleriesListDisable = true;
		this.onSubmitSpinner = false;
	}

	onSubmit(): void {
		this.onSubmitSpinner = true;

		if (this.shootProofBrands) {
			if (this.selectedGalleryId === -1) {
				this.messageService.showToaster('e_2', 'shootProof');
				this.onSubmitSpinner = false;
				return;
			}
			const galleryParamsToSend = {
				jobId: this.jobId,
				spGalleryId: +this.selectedGalleryId,
				spBrandId: +this.selectedShootProofBrandId,
				importOrders: false
			};

			this.shootProofDataService
				.selectExistingGaleryV1(galleryParamsToSend)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize(() => (this.onSubmitSpinner = false))
				)
				.subscribe(
					(): void => this.successSelectExistingShootProofGallery(),
					error => this.checkIfShootProofError(error)
				);
		} else {
			if (this.selectedGalleries.length === 0) {
				this.messageService.showToaster('e_6', 'picTime');
				this.onSubmitSpinner = false;
				return;
			}
			const selectedPicTimeGalleriesToSend = {
				galleryIds: this.selectedGalleries
			};
			this.pickTimeDataProvider
				.importSelectedPicTimeGalleries(
					this.jobId,
					selectedPicTimeGalleriesToSend
				)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize(() => (this.onSubmitSpinner = false))
				)
				.subscribe(
					(): void => this.successSelectExistingPicTimeGalleries(),
					error => this.messageService.handleError(error)
				);
		}
	}

	private successSelectExistingShootProofGallery(): void {
		this.messageService.showToaster('s_3', 'shootProof');
		this.integrationService.emitGalleriesUpdateEvent('UPDATE_SN_GALLERIES');
		this.invoiceDataService.emitInvoiceUpdateEvent('UPDATE_SP_INVOICES');
		this.dismiss();
	}

	private successSelectExistingPicTimeGalleries(): void {
		this.messageService.showToaster('s_2', 'picTime');
		this.integrationService.emitGalleriesUpdateEvent('UPDATE_SN_GALLERIES');
		this.invoiceDataService.emitInvoiceUpdateEvent('UPDATE_PT_INVOICES');
		this.dismiss();
	}

	private checkIfShootProofError(error): void {
		if (error.status === 500) {
			this.messageService.showToaster('e_1', 'shootProof');
		} else {
			this.messageService.showDefaultErrorMessage(error);
		}
	}

	disableSubmit(): boolean {
		let helper = false;
		if (
			this.shootProofBrands &&
			(this.selectedGalleryId === -1 && this.selectedShootProofBrandId === -1)
		) {
			helper = true;
		} else if (
			!this.shootProofBrands &&
			this.picTimeGalleries.length === 0 &&
			this.selectedGalleries.length === 0
		) {
			helper = true;
		}
		return helper;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=gh4osmq0bhk!./web/modern/components/integrations/connect-galleries/modals/select-existing-gallery-modal/select-existing-gallery.modal.ts